import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { GeneralActivitiesGridResponse, GeneralActivityMember } from '@time-tracking/shared';

import { useGeneralActivitiesGrid } from '../../../api/general-activities.js';
import { AlertTriangleIcon } from '../../../components/icons/AlertTriangleIcon.js';
import { InfoIcon } from '../../../components/icons/InfoIcon.js';
import { ActivityMatrix, type ActivityMatrixHandle } from './ActivityMatrix.js';
import { GlobeIcon, LayersIcon, SearchIcon, ShieldIcon } from './icons.js';
import { gridGroups, NO_TEAM_GROUP_ID } from './model.js';
import { TurnOnDialog, UnassignDialog } from './UnassignDialog.js';
import { useGrantWrites, type GrantToast } from './useGrantWrites.js';
import './general-activities.css';

const CATEGORIES_PANE_PATH = '/admin/categories';

const SKELETON_ROWS = 5;
const SKELETON_CELLS = 3;

export interface GeneralActivitiesViewProps {
  /** Administrators see every team, the No team group, and the Categories links. */
  isAdmin: boolean;
  /** The page's toast region: every grant write reports through it. */
  onToast: GrantToast;
}

function scopeSentence(isAdmin: boolean, grid: GeneralActivitiesGridResponse | undefined): string {
  if (!grid) return '';
  const n = grid.teams.length;
  if (isAdmin) return `You are viewing all ${n} ${n === 1 ? 'team' : 'teams'} as an administrator.`;
  const names = grid.teams.map((t) => t.name).join(' and ');
  return `You are viewing ${n > 1 ? `your ${n} teams` : 'your team'}: ${names}.`;
}

/**
 * Projects & tasks ▸ General activities: who gets each category that
 * administrators set to "Assigned by leads". Prototype:
 * design_handoff_general_activities/src/general-activities.jsx.
 */
export function GeneralActivitiesView({ isAdmin, onToast }: GeneralActivitiesViewProps) {
  const gridQuery = useGeneralActivitiesGrid();
  const grid = gridQuery.data;
  const teamCount = grid?.teams.length ?? 0;

  const matrixRef = useRef<ActivityMatrixHandle>(null);
  const focusCell = useCallback((cellKey: string) => matrixRef.current?.focusCell(cellKey), []);
  const writes = useGrantWrites({ grid, onToast, focusCell });

  const [filter, setFilter] = useState('');
  const query = filter.trim().toLowerCase();
  const memberFilter = (m: GeneralActivityMember) =>
    !query ||
    m.displayName.toLowerCase().includes(query) ||
    (m.utEid?.toLowerCase().includes(query) ?? false);
  const jumpHintId = useId();

  let body: ReactNode;
  if (gridQuery.isPending) {
    body = (
      <div className="ga-scroll ga-loading" aria-busy="true">
        <div className="ga-loading__head" role="status">
          <span className="ga-spin" aria-hidden="true" /> Loading your teams…
        </div>
        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <div key={i} className="ga-loading__row" aria-hidden="true">
            <span className="ga-skel" style={{ width: 180 }} />
            {Array.from({ length: SKELETON_CELLS }, (_, j) => (
              <span key={j} className="ga-skel" style={{ width: 28 }} />
            ))}
          </div>
        ))}
      </div>
    );
  } else if (gridQuery.isError || !grid) {
    body = (
      <div className="ga-state err" role="alert">
        <span className="ga-state__icon ga-state__icon--warn">
          <AlertTriangleIcon className="ga-ico-24" />
        </span>
        <h3>Couldn’t load your teams</h3>
        <p>
          Something went wrong reaching the server. Nothing was changed, and everyone’s current
          assignments are safe.
        </p>
        <button
          type="button"
          className="btn btn--primary ga-state__action"
          onClick={() => void gridQuery.refetch()}
          disabled={gridQuery.isFetching}
        >
          Try again
        </button>
      </div>
    );
  } else if (grid.activities.length === 0) {
    body = (
      <div className="ga-state">
        <span className="ga-state__icon">
          <LayersIcon />
        </span>
        <h3>No activities are assigned by leads yet</h3>
        <p>
          {isAdmin
            ? 'Administrators choose which activities leads can assign. Until one is set to “Assigned by leads”, there is nothing to do here.'
            : 'Ask an administrator.'}
        </p>
        {isAdmin && (
          <Link className="btn btn--primary ga-state__action" to={CATEGORIES_PANE_PATH}>
            Open Categories &amp; classifications
          </Link>
        )}
      </div>
    );
  } else {
    const fixed = grid.everyoneCategories.length + grid.approversOnlyCategories.length;
    body = (
      <>
        {fixed > 0 && (
          <div className="ga-everyone">
            <span>Already set by administrators, nothing to assign:</span>
            <ul className="ga-chips" aria-label="Activities already set by administrators">
              {grid.everyoneCategories.map((c) => (
                <li key={c.categoryId} className="ga-chip" title="On everyone’s timesheet">
                  <GlobeIcon />
                  {c.name}
                  <span className="sr-only">, on everyone’s timesheet</span>
                </li>
              ))}
              {grid.approversOnlyCategories.map((c) => (
                <li key={c.categoryId} className="ga-chip appr" title="Approvers only">
                  <ShieldIcon />
                  {c.name} · approvers only
                </li>
              ))}
            </ul>
          </div>
        )}
        {showFind(isAdmin, grid) && (
          <FindPeople
            grid={grid}
            filter={filter}
            onFilterChange={setFilter}
            matchCount={query ? matchCount(grid, memberFilter) : null}
            jumpHintId={jumpHintId}
            onJump={(groupId) => matrixRef.current?.jumpToGroup(groupId)}
          />
        )}
        <ActivityMatrix
          grid={grid}
          onTogglePerson={writes.onTogglePerson}
          onToggleTeam={writes.onToggleTeam}
          memberFilter={memberFilter}
          filterQuery={filter}
          busyKeys={writes.busyKeys}
          spinnerKeys={writes.spinnerKeys}
          matrixRef={matrixRef}
        />
      </>
    );
  }

  const scope = scopeSentence(isAdmin, grid);
  const whose = isAdmin ? 'every team' : teamCount > 1 ? 'your teams' : 'your team';

  return (
    <section className="ga-view" aria-labelledby="ga-title">
      <div className="ga-head">
        <h2 id="ga-title">General activities</h2>
        <p>
          Choose who gets each activity that administrators have set to <b>Assigned by leads</b>.{' '}
          {scope ? `${scope} ` : ''}Changes apply to timesheets not yet submitted.
        </p>
      </div>

      {body}

      {/* Saving… then the outcome, for screen readers; the toast has the detail. */}
      <div className="sr-only" role="status">
        {writes.status}
      </div>

      <div className="ga-note">
        <InfoIcon className="ga-ico-14" />
        <span>
          The list of activities is managed by administrators — leads choose who gets them, not what
          they are called, whether they are locked, or whether they exist.{' '}
          {isAdmin ? (
            <Link to={CATEGORIES_PANE_PATH}>
              Open Administration ▸ Categories &amp; classifications
            </Link>
          ) : (
            'To add or change an activity, ask an administrator.'
          )}{' '}
          You only see people on {whose}. Someone on two teams appears under both, and either lead
          can change their activities.
        </span>
      </div>
      <GrantDialogs writes={writes} />
    </section>
  );
}

/**
 * The finding row — administrators, and approvers with more than one team
 * (the handoff's rule; a single-team approver has nothing to find).
 */
function showFind(isAdmin: boolean, grid: GeneralActivitiesGridResponse): boolean {
  return isAdmin || grid.teams.length > 1;
}

/** Distinct people matching the filter, each counted once however many teams they are on. */
function matchCount(
  grid: GeneralActivitiesGridResponse,
  memberFilter: (m: GeneralActivityMember) => boolean,
): number {
  const ids = new Set<string>();
  for (const g of gridGroups(grid))
    for (const m of g.members) if (memberFilter(m)) ids.add(m.userId);
  return ids.size;
}

function FindPeople({
  grid,
  filter,
  onFilterChange,
  matchCount: count,
  jumpHintId,
  onJump,
}: {
  grid: GeneralActivitiesGridResponse;
  filter: string;
  onFilterChange: (value: string) => void;
  /** Null while no query is set. */
  matchCount: number | null;
  jumpHintId: string;
  onJump: (groupId: string) => void;
}) {
  const [jumpTarget, setJumpTarget] = useState('');
  return (
    <div className="ga-find" role="search" aria-label="Find people">
      <label className="ga-filter">
        <span className="sr-only">Filter people by name or EID</span>
        <SearchIcon />
        <input
          type="search"
          placeholder="Filter people…"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
        />
      </label>
      <div className="ga-jump">
        <select
          aria-label="Jump to team"
          aria-describedby={jumpHintId}
          value={jumpTarget}
          onChange={(e) => setJumpTarget(e.target.value)}
        >
          <option value="">Jump to team</option>
          {grid.teams.map((t) => (
            <option key={t.teamId} value={t.teamId}>
              {t.name}
            </option>
          ))}
          {grid.noTeam && <option value={NO_TEAM_GROUP_ID}>No team</option>}
        </select>
        <button
          type="button"
          className="ga-jump__go"
          disabled={!jumpTarget}
          onClick={() => {
            onJump(jumpTarget);
            setJumpTarget('');
          }}
        >
          Go
        </button>
        <span id={jumpHintId} hidden>
          Go moves focus to that team in the table.
        </span>
      </div>
      {/* Mounted empty so the first count is announced (a live region inserted
          already populated is dropped by screen readers). */}
      <span className="ga-match" aria-live="polite">
        {count === null ? '' : `${count} ${count === 1 ? 'person matches' : 'people match'}`}
      </span>
    </div>
  );
}

function GrantDialogs({ writes }: { writes: ReturnType<typeof useGrantWrites> }) {
  const d = writes.dialog;
  if (!d) return null;
  const common = {
    activityName: d.target.activity.name,
    changed: d.changed,
    saving: d.saving,
    onConfirm: writes.confirmDialog,
    onCancel: writes.cancelDialog,
  };
  switch (d.kind) {
    case 'unassignPerson':
      return (
        <UnassignDialog
          {...common}
          kind="person"
          personName={d.target.member.displayName}
          ownTeamId={d.target.groupId === NO_TEAM_GROUP_ID ? null : d.target.groupId}
          preview={d.preview}
          failed={false}
          onRetry={() => undefined}
        />
      );
    case 'unassignTeam':
      return (
        <UnassignDialog
          {...common}
          kind="team"
          teamName={d.target.team.name}
          preview={d.preview}
          failed={d.failed}
          onRetry={writes.retryTeamImpact}
        />
      );
    case 'turnOn':
      return <TurnOnDialog {...common} teamName={d.target.team.name} preview={d.preview} />;
  }
}
